import React from 'react'
import { Link } from 'react-router-dom'
import { SEOHead } from '../components/ui/SEOHead'
import { TerminalWindow } from '../components/terminal/TerminalWindow'
import { AsciiDivider } from '../components/terminal/AsciiDivider'

const EFFECTIVE_DATE = 'May 2026'

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <div
      style={{
        color: 'var(--accent-cyan)',
        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        fontSize: '0.88rem',
        fontWeight: 700,
        marginBottom: '10px',
      }}
    >
      ## {title}
    </div>
    <div
      style={{
        color: 'var(--text-secondary)',
        fontSize: '0.875rem',
        lineHeight: '1.85',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  </section>
)

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '6px' }}>
        {item}
      </li>
    ))}
  </ul>
)

const ProhibitedItem: React.FC<{ label: string; description: string }> = ({ label, description }) => (
  <li style={{ marginBottom: '8px' }}>
    <span
      style={{
        color: 'var(--accent-red, #ff4444)',
        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        fontSize: '0.8rem',
        fontWeight: 700,
      }}
    >
      [{label}]
    </span>{' '}
    {description}
  </li>
)

export const TermsPage: React.FC = () => (
  <>
    <SEOHead
      title="Terms and Conditions"
      description="CodeToByte Terms and Conditions — rules for using our website, commenting, and community standards."
      url="/terms"
    />

    <div style={{ maxWidth: '780px', margin: '0 auto', padding: '32px 0' }}>
      <TerminalWindow title="terms-and-conditions.md">
        <div
          style={{
            fontFamily: "'JetBrains Mono', 'Courier New', monospace",
            fontSize: '0.85rem',
            lineHeight: '1.8',
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: '24px' }}>
            <div
              style={{
                fontSize: '1.15rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: '6px',
              }}
            >
              Terms and Conditions
            </div>
            <div style={{ color: 'var(--text-muted, #555)', fontSize: '0.78rem' }}>
              Last updated: {EFFECTIVE_DATE}
            </div>
          </div>

          <AsciiDivider char="─" width={50} />

          {/* Intro */}
          <div
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
              lineHeight: '1.85',
              marginBottom: '28px',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Please read these Terms and Conditions carefully before using{' '}
            <strong>codetobyte.com</strong>. By accessing or using any part of this website,
            you agree to be bound by these terms. If you do not agree, please do not use the site.
          </div>

          <Section title="Acceptance of Terms">
            <p style={{ marginTop: 0 }}>
              These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of
              CodeToByte, including its content, features, and community tools (such as comments
              and newsletter). We may update these Terms at any time. Continued use of the site
              after changes are posted constitutes your acceptance of the revised Terms.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Use of the Website">
            <p style={{ marginTop: 0 }}>
              You may use CodeToByte for lawful, personal, and non-commercial purposes. You
              agree not to:
            </p>
            <BulletList
              items={[
                'Attempt to gain unauthorised access to any part of the site or its infrastructure.',
                'Scrape, crawl, or harvest data from the site in a manner that disrupts service.',
                'Use the site to distribute malware, spam, or any harmful content.',
                'Impersonate another person or entity.',
                'Use automated tools to create accounts or post comments.',
              ]}
            />
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="User Accounts">
            <p style={{ marginTop: 0 }}>
              To post comments, you must sign in using your Google account. You are responsible
              for maintaining the security of your Google account and for all activity that occurs
              under it. You must provide accurate information and must not create accounts to
              circumvent bans or suspensions.
            </p>
            <p>
              We reserve the right to suspend or terminate any account that violates these Terms,
              without prior notice.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Comments Policy">
            <p style={{ marginTop: 0 }}>
              CodeToByte&rsquo;s comments section exists to foster thoughtful, constructive
              discussion about software engineering and technology. We welcome debate,
              disagreement, and diverse perspectives — but we expect all participants to treat
              each other with respect.
            </p>

            <div
              style={{
                color: 'var(--accent-cyan)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '16px 0 8px',
                fontWeight: 600,
              }}
            >
              ### Be a good community member
            </div>
            <BulletList
              items={[
                'Engage with ideas, not people. Critique arguments, not individuals.',
                'Keep discussion relevant to the article or topic at hand.',
                'Share your expertise generously — help others learn.',
                'Disagree constructively. Explain your reasoning with facts and examples.',
                'Give credit when referencing others\' work or ideas.',
              ]}
            />

            <div
              style={{
                color: 'var(--accent-red, #ff4444)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '16px 0 8px',
                fontWeight: 600,
              }}
            >
              ### Prohibited Content
            </div>
            <p>
              The following types of content are strictly prohibited and will be removed.
              CodeToByte has zero tolerance for:
            </p>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <ProhibitedItem
                label="Hate Speech"
                description="Content that attacks, demeans, or incites hatred against any person or group based on race, ethnicity, national origin, gender, gender identity, sexual orientation, religion, disability, age, or any other protected characteristic."
              />
              <ProhibitedItem
                label="Racism & Discrimination"
                description="Racist, xenophobic, or otherwise discriminatory language or imagery of any kind."
              />
              <ProhibitedItem
                label="Sexism & Misogyny"
                description="Sexist remarks, gender-based harassment, or content that demeans people based on gender or gender identity."
              />
              <ProhibitedItem
                label="Harassment & Bullying"
                description="Targeting, intimidating, threatening, or persistently harassing any individual, whether they are a community member, contributor, or public figure."
              />
              <ProhibitedItem
                label="Threats & Violence"
                description="Any content that threatens physical harm, promotes violence, or glorifies violent acts against any person or group."
              />
              <ProhibitedItem
                label="Abusive Language"
                description="Excessive profanity, personal insults, or language designed to demean or degrade another person."
              />
              <ProhibitedItem
                label="Doxxing"
                description="Sharing private or personally identifiable information about another person without their explicit consent."
              />
              <ProhibitedItem
                label="Spam & Self-Promotion"
                description="Repetitive posts, unsolicited advertising, affiliate links, or promotional content not relevant to the discussion."
              />
              <ProhibitedItem
                label="Misinformation"
                description="Deliberately spreading false or misleading technical information."
              />
              <ProhibitedItem
                label="Illegal Content"
                description="Any content that violates applicable laws, including content related to piracy, fraud, or the distribution of illegal material."
              />
            </ul>

            <div
              style={{
                color: 'var(--accent-cyan)',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                margin: '16px 0 8px',
                fontWeight: 600,
              }}
            >
              ### Moderation and Enforcement
            </div>
            <p>
              Our moderators review comments to ensure compliance with these Terms. Comments that
              violate our policy will be removed, and repeated or severe violations will result
              in escalating consequences:
            </p>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Comment Removal</strong> — The offending comment is removed.
                The user may be notified of the violation.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Temporary Account Suspension</strong> — Continued or repeated violations
                result in a temporary suspension of commenting privileges.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Indefinite Suspension</strong> — Further violations following a temporary
                suspension will result in an indefinite suspension of the account.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Permanent Ban</strong> — Any subsequent violation after an indefinite
                suspension, or a single sufficiently severe violation (e.g., credible threats,
                doxxing, hate speech), will result in a permanent ban from the platform.
              </li>
            </ol>
            <p>
              We reserve the right to skip steps and issue immediate permanent bans for
              particularly severe violations. Moderation decisions are at the sole discretion
              of CodeToByte.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Newsletter Subscription">
            <p style={{ marginTop: 0 }}>
              By subscribing to the CodeToByte newsletter, you agree to receive periodic emails
              about new articles and updates. You may unsubscribe at any time using the link
              included in every email. We will never send unsolicited emails or share your email
              address with third parties for marketing purposes.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Intellectual Property">
            <p style={{ marginTop: 0 }}>
              All articles, text, graphics, and other content published on CodeToByte are the
              property of CodeToByte and its contributors, unless otherwise stated. You may share
              links to articles and quote brief excerpts with attribution, but you may not
              reproduce full articles without prior written permission.
            </p>
            <p>
              By posting a comment, you grant CodeToByte a non-exclusive, royalty-free,
              perpetual licence to display and moderate that comment on the site. You retain
              ownership of your comment content.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Disclaimer of Warranties">
            <p style={{ marginTop: 0 }}>
              CodeToByte is provided &ldquo;as is&rdquo; without any warranty, express or
              implied. We do not guarantee that the site will be uninterrupted, error-free, or
              free of harmful components. Articles represent the opinions and knowledge of their
              authors at the time of writing and may become outdated. Always verify technical
              information independently before applying it in a production environment.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Limitation of Liability">
            <p style={{ marginTop: 0 }}>
              To the fullest extent permitted by law, CodeToByte and its contributors shall not
              be liable for any indirect, incidental, special, consequential, or punitive damages
              arising from your use of, or inability to use, this website or its content.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Age Requirement">
            <p style={{ marginTop: 0 }}>
              You must be at least 13 years of age to create an account or interact with the
              community features of CodeToByte. By signing in or subscribing, you confirm that
              you meet this requirement.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Governing Law">
            <p style={{ marginTop: 0 }}>
              These Terms are governed by applicable law. Any disputes arising from your use of
              CodeToByte shall be resolved in good faith. If resolution cannot be reached
              informally, disputes shall be subject to the jurisdiction of the courts competent
              for the location of CodeToByte&rsquo;s operator.
            </p>
          </Section>

          <AsciiDivider char="─" width={50} />

          <Section title="Contact Us">
            <p style={{ marginTop: 0 }}>
              If you have any questions about these Terms, wish to report a policy violation, or
              need to reach our moderation team, please contact:
            </p>
            <div
              style={{
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: '0.82rem',
                color: 'var(--text-secondary)',
                marginTop: '8px',
              }}
            >
              <div>
                <span style={{ color: 'var(--accent-green)' }}>email</span>{' '}
                →{' '}
                <a
                  href="mailto:support@codetobyte.com"
                  style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
                >
                  support@codetobyte.com
                </a>
              </div>
              <div style={{ marginTop: '4px' }}>
                <span style={{ color: 'var(--accent-green)' }}>website</span>{' '}
                → codetobyte.com
              </div>
            </div>
          </Section>

          {/* Back link */}
          <div style={{ marginTop: '8px' }}>
            <Link
              to="/"
              style={{
                color: 'var(--accent-blue)',
                fontSize: '0.8rem',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              }}
            >
              [← back to home]
            </Link>
            <Link
              to="/privacy"
              style={{
                color: 'var(--accent-blue)',
                fontSize: '0.8rem',
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                marginLeft: '12px',
              }}
            >
              [privacy policy →]
            </Link>
          </div>
        </div>
      </TerminalWindow>
    </div>
  </>
)
